import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import api from "../services/api";
import { readFileAsync } from "../utils";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import {
  FaBold,
  FaItalic,
  FaLink,
  FaImage,
  FaListUl,
  FaListOl,
  FaQuoteLeft,
  FaCode,
  FaHeading,
  FaParagraph,
  FaIndent,
  FaOutdent,
  FaUnderline,
  FaStrikethrough,
} from "react-icons/fa";
import { Extension } from "@tiptap/core";

// Custom extension for resizable images
const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...Image.config.addAttributes(),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width"),
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {};
          }
          return { width: attributes.width };
        },
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height"),
        renderHTML: (attributes) => {
          if (!attributes.height) {
            return {};
          }
          return { height: attributes.height };
        },
      },
    };
  },
});

// Custom extension to handle Enter key behavior
const CustomKeyboardShortcuts = Extension.create({
  name: "customKeyboardShortcuts",

  addOptions() {
    return {
      onSubmit: null,
    };
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        if (this.options.onSubmit && typeof this.options.onSubmit === "function") {
          this.options.onSubmit();
          return true; // Prevent default behavior
        }
        return false; // Allow default behavior
      },
      "Shift-Enter": () => {
        return this.editor.commands.first(({ commands }) => [
          () => commands.newlineInCode(),
          () => commands.createParagraphNear(),
          () => commands.liftEmptyBlock(),
          () => commands.splitBlock(),
        ]);
      },
    };
  },
});

const MenuBar = ({ editor, buttonList = [], handleImageUpload }) => {
  if (!editor) return null;

  const fileInputRef = React.useRef(null);

  // Définir tous les boutons supportés
  const supportedButtons = {
    bold: {
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive("bold"),
      icon: <FaBold size={16} />,
      tooltip: "Bold",
    },
    italic: {
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive("italic"),
      icon: <FaItalic size={16} />,
      tooltip: "Italic",
    },
    link: {
      action: () => {
        const url = window.prompt("URL");
        if (url) {
          editor.chain().focus().setLink({ href: url }).run();
        } else if (url === "") {
          editor.chain().focus().unsetLink().run();
        }
      },
      isActive: () => editor.isActive("link"),
      icon: <FaLink size={16} />,
      tooltip: "Link",
    },
    image: {
      action: () => {
        fileInputRef.current.click();
      },
      isActive: () => editor.isActive("image"),
      icon: <FaImage size={16} />,
      tooltip: "Image",
    },
    bulletList: {
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive("bulletList"),
      icon: <FaListUl size={16} />,
      tooltip: "Bullet List",
    },
    orderedList: {
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive("orderedList"),
      icon: <FaListOl size={16} />,
      tooltip: "Ordered List",
    },
    heading1: {
      action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.isActive("heading", { level: 1 }),
      icon: <span className="font-bold">H1</span>,
      tooltip: "Heading 1",
    },
    heading2: {
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.isActive("heading", { level: 2 }),
      icon: <span className="font-bold">H2</span>,
      tooltip: "Heading 2",
    },
    heading3: {
      action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.isActive("heading", { level: 3 }),
      icon: <span className="font-bold">H3</span>,
      tooltip: "Heading 3",
    },
    paragraph: {
      action: () => editor.chain().focus().setParagraph().run(),
      isActive: () => editor.isActive("paragraph"),
      icon: <FaParagraph size={16} />,
      tooltip: "Paragraph",
    },
    blockquote: {
      action: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: () => editor.isActive("blockquote"),
      icon: <FaQuoteLeft size={16} />,
      tooltip: "Blockquote",
    },
    codeBlock: {
      action: () => editor.chain().focus().toggleCodeBlock().run(),
      isActive: () => editor.isActive("codeBlock"),
      icon: <FaCode size={16} />,
      tooltip: "Code Block",
    },
    indent: {
      action: () => editor.chain().focus().indent().run(),
      icon: <FaIndent size={16} />,
      tooltip: "Indent",
    },
    outdent: {
      action: () => editor.chain().focus().outdent().run(),
      icon: <FaOutdent size={16} />,
      tooltip: "Outdent",
    },
  };

  // Si aucun bouton spécifique n'est fourni, utiliser tous les boutons disponibles
  const buttonsToShow = buttonList.length > 0 ? buttonList.flat() : Object.keys(supportedButtons);

  // Organiser les boutons en groupes pour un style Word
  const buttonGroups = [
    ["paragraph", "heading1", "heading2", "heading3"],
    ["bold", "italic"],
    ["bulletList", "orderedList", "indent", "outdent"],
    ["blockquote", "codeBlock"],
    ["link", "image"],
  ];

  // Filtrer les groupes pour n'inclure que les boutons demandés ou tous si aucun n'est spécifié
  const filteredGroups = buttonList.length > 0 ? buttonGroups.map((group) => group.filter((btn) => buttonsToShow.includes(btn))).filter((group) => group.length > 0) : buttonGroups;

  return (
    <div className="tiptap-menu flex flex-wrap items-center gap-2 p-1.5 border-b border-gray-200 bg-white">
      {filteredGroups.map((group, groupIndex) => (
        <React.Fragment key={groupIndex}>
          <div className="flex gap-1">
            {group.map((button) => {
              if (!supportedButtons[button]) return null;
              const { action, isActive, icon, tooltip } = supportedButtons[button];

              return (
                <button
                  type="button"
                  key={button}
                  onClick={action}
                  className={`p-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors ${isActive && isActive() ? "bg-blue-50 text-blue-600 border border-blue-200" : ""}`}
                  title={tooltip}>
                  {icon}
                </button>
              );
            })}
          </div>
          {groupIndex < filteredGroups.length - 1 && <div className="border-r border-gray-200 h-6 mx-1"></div>}
        </React.Fragment>
      ))}
      <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
    </div>
  );
};

export default function TipTapEditor({ values, onChange, buttonList = [], placeholder = "", height = "h-52", onSubmit }) {
  const { user } = useSelector((state) => state.Auth);
  const [uploading, setUploading] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState(null);
  const [resizeHandles, setResizeHandles] = React.useState({ visible: false, top: 0, left: 0 });
  const editorRef = React.useRef(null);
  const editorContainerRef = useRef(null);
  const [contentHeight, setContentHeight] = React.useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
      }),
      ResizableImage,
      Placeholder.configure({
        placeholder,
      }),
      CustomKeyboardShortcuts.configure({
        onSubmit,
      }),
    ],
    content: values,
    parseOptions: {
      preserveWhitespace: "full",
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const sanitizedHtml = DOMPurify.sanitize(html);
      onChange(sanitizedHtml);

      // Débouncer la mise à jour de la hauteur pour éviter les conflits
      setTimeout(updateContentHeight, 100);
    },
  });

  // Function to measure and update content height
  const updateContentHeight = () => {
    if (editor && editorContainerRef.current) {
      const editorElement = document.querySelector(".ProseMirror");
      if (editorElement) {
        const newHeight = editorElement.scrollHeight;
        setContentHeight(newHeight);
      }
    }
  };

  useEffect(() => {
    if (editor && values !== editor.getHTML()) {
      // Sauvegarder la position actuelle du curseur
      const { from, to } = editor.state.selection;
      const currentHTML = editor.getHTML();

      // Ne mettre à jour que si le contenu est vraiment différent
      if (values !== currentHTML) {
        editor.commands.setContent(values, false, { preserveWhitespace: "full" });

        // Restaurer la position du curseur si possible
        try {
          if (from !== undefined && to !== undefined) {
            const docSize = editor.state.doc.content.size;
            const safeFrom = Math.min(from, docSize);
            const safeTo = Math.min(to, docSize);
            editor.commands.setTextSelection({ from: safeFrom, to: safeTo });
          }
        } catch (error) {
          console.log("Could not restore cursor position");
        }
      }

      // Update height after content is set
      setTimeout(updateContentHeight, 50);
    }
  }, [values, editor]);

  // Observer for newly added images and height tracking
  useEffect(() => {
    if (!editor) return;

    const editorElement = document.querySelector(".ProseMirror");
    if (!editorElement) return;

    // Débouncer les mises à jour de hauteur
    let resizeTimeout;
    const debouncedUpdateHeight = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateContentHeight, 100);
    };

    // Create a ResizeObserver to detect size changes
    const resizeObserver = new ResizeObserver(debouncedUpdateHeight);
    resizeObserver.observe(editorElement);

    // For image loading events
    const handleImageLoad = () => {
      debouncedUpdateHeight();
    };

    // Observer for new images
    const observer = new MutationObserver((mutations) => {
      let shouldUpdateHeight = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          const addedNodes = Array.from(mutation.addedNodes);
          addedNodes.forEach((node) => {
            if (node.nodeName === "IMG") {
              // Add load event and resize handlers
              node.addEventListener("load", handleImageLoad);
              attachImageHandlers(node);
              shouldUpdateHeight = true;
            } else if (node.querySelectorAll) {
              const images = node.querySelectorAll("img");
              images.forEach((img) => {
                img.addEventListener("load", handleImageLoad);
                attachImageHandlers(img);
              });
              if (images.length > 0) shouldUpdateHeight = true;
            }
          });
        }
      });

      if (shouldUpdateHeight) {
        debouncedUpdateHeight();
      }
    });

    observer.observe(editorElement, { childList: true, subtree: true });

    // Process existing images
    const images = editorElement.querySelectorAll("img");
    images.forEach((img) => {
      img.addEventListener("load", handleImageLoad);
      attachImageHandlers(img);
    });

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
      observer.disconnect();
      const images = editorElement.querySelectorAll("img");
      images.forEach((img) => {
        img.removeEventListener("load", handleImageLoad);
      });
    };
  }, [editor]);

  const attachImageHandlers = (imageElement) => {
    if (!imageElement._resizeHandlersAttached) {
      imageElement.addEventListener("click", (e) => {
        e.stopPropagation();
        selectImage(imageElement);
      });
      imageElement._resizeHandlersAttached = true;
    }
  };

  const selectImage = (imageElement) => {
    // Désélectionner l'image précédente si elle existe
    if (selectedImage) {
      selectedImage.classList.remove("selected-image");
    }

    // Sélectionner la nouvelle image
    imageElement.classList.add("selected-image");
    setSelectedImage(imageElement);

    // Positionner les poignées de redimensionnement
    updateResizeHandlesPosition(imageElement);

    // Sélectionner l'image dans l'éditeur
    const pos = editor.view.posAtDOM(imageElement, 0);
    if (pos !== undefined) {
      editor.chain().setNodeSelection(pos).run();
    }
  };

  const updateResizeHandlesPosition = (imageElement) => {
    if (!imageElement) return;

    const rect = imageElement.getBoundingClientRect();
    const editorRect = editorContainerRef.current.getBoundingClientRect();

    setResizeHandles({
      visible: true,
      top: rect.bottom - editorRect.top,
      left: rect.right - editorRect.left,
    });
  };

  // Désélectionner l'image quand on clique ailleurs
  useEffect(() => {
    if (!editor) return;

    const handleGlobalClick = (e) => {
      if (selectedImage && !e.target.closest("img") && !e.target.closest(".resize-handle")) {
        selectedImage.classList.remove("selected-image");
        setSelectedImage(null);
        setResizeHandles({ visible: false, top: 0, left: 0 });
      }
    };

    document.addEventListener("click", handleGlobalClick);

    return () => {
      document.removeEventListener("click", handleGlobalClick);
    };
  }, [editor, selectedImage]);

  editorRef.current = editor;

  // Upload image helper function that can be reused
  const uploadImage = async (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }

    setUploading(true);
    try {
      // Convert file to base64
      const fileData = await readFileAsync(file);

      // Get project ID from URL or context if available
      const projectId = window.location.pathname.split("/").find((segment) => segment.match(/^[0-9a-fA-F]{24}$/)) || "default";

      // Upload to S3 via API
      const { data: urls } = await api.post("/file/multiple", {
        files: [{ rawBody: fileData, name: file.name }],
        folder: `/project/${projectId}/editor`,
      });

      if (urls && urls.length > 0 && editorRef.current) {
        // Insert image URL into editor
        editorRef.current.chain().focus().setImage({ src: urls[0] }).run();

        // Positionner le curseur après l'image pour éviter qu'elle soit remplacée lors de la saisie
        setTimeout(() => {
          if (editorRef.current) {
            // Chercher la position de l'image qui vient d'être insérée
            const { selection } = editorRef.current.state;
            const { $from } = selection;

            // Si on est sur un noeud image, se déplacer après
            if ($from.parent.type.name === "image" || ($from.nodeAfter && $from.nodeAfter.type.name === "image")) {
              editorRef.current
                .chain()
                .focus()
                .setTextSelection($from.pos + 1)
                .run();
            } else {
              // Sinon, chercher l'image dans le document et se positionner après
              let imagePos = null;
              editorRef.current.state.doc.descendants((node, pos) => {
                if (node.type.name === "image" && node.attrs.src === urls[0]) {
                  imagePos = pos;
                  return false; // Arrêter la recherche
                }
              });

              if (imagePos !== null) {
                // Se positionner après l'image
                editorRef.current
                  .chain()
                  .focus()
                  .setTextSelection(imagePos + 1)
                  .run();
              }
            }
          }
        }, 100); // Petit délai pour s'assurer que l'image est bien insérée

        toast.success("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    await uploadImage(files[0]);
    // Clear input value to allow uploading the same file again
    event.target.value = "";
  };

  // Handle drag and drop of images directly into the editor
  const handleDrop = async (event) => {
    if (!editor || uploading) return;

    // Prevent default browser behavior
    event.preventDefault();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    await uploadImage(files[0]);
  };

  // Handle paste events for images
  const handlePaste = async (event) => {
    if (!editor || uploading) return;

    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        // Get the image file from clipboard
        const file = items[i].getAsFile();
        if (file) {
          // Prevent default paste behavior for images
          event.preventDefault();
          await uploadImage(file);
          break;
        }
      }
    }
  };

  // Image resize function updates - add height update after resize
  const resizeImage = (width, height) => {
    if (editor && selectedImage) {
      const pos = editor.view.posAtDOM(selectedImage, 0);
      if (pos !== undefined) {
        editor.chain().setNodeSelection(pos).run();
        editor.chain().updateAttributes("image", { width, height }).run();

        // Add a small delay to ensure the resize has been applied to the DOM
        setTimeout(updateContentHeight, 50);
      }
    }
  };

  // Handle resize operation
  const startResize = (e, direction) => {
    if (!selectedImage) return;

    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = selectedImage.offsetWidth;
    const startHeight = selectedImage.offsetHeight;
    const aspectRatio = startWidth / startHeight;
    const originalAspectRatio = aspectRatio;

    // Points de départ pour le positionnement
    const startLeft = selectedImage.offsetLeft;
    const startTop = selectedImage.offsetTop;

    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();

      let newWidth = startWidth;
      let newHeight = startHeight;

      // Calculer les nouvelles dimensions en fonction de la direction
      switch (direction) {
        case "se": // Sud-est (coin inférieur droit)
          newWidth = startWidth + (moveEvent.clientX - startX);
          newHeight = startHeight + (moveEvent.clientY - startY);
          break;
        case "sw": // Sud-ouest (coin inférieur gauche)
          newWidth = startWidth - (moveEvent.clientX - startX);
          newHeight = startHeight + (moveEvent.clientY - startY);
          break;
        case "ne": // Nord-est (coin supérieur droit)
          newWidth = startWidth + (moveEvent.clientX - startX);
          newHeight = startHeight - (moveEvent.clientY - startY);
          break;
        case "nw": // Nord-ouest (coin supérieur gauche)
          newWidth = startWidth - (moveEvent.clientX - startX);
          newHeight = startHeight - (moveEvent.clientY - startY);
          break;
        case "n": // Nord (milieu supérieur)
          newHeight = startHeight - (moveEvent.clientY - startY);
          newWidth = newHeight * originalAspectRatio;
          break;
        case "s": // Sud (milieu inférieur)
          newHeight = startHeight + (moveEvent.clientY - startY);
          newWidth = newHeight * originalAspectRatio;
          break;
        case "e": // Est (milieu droit)
          newWidth = startWidth + (moveEvent.clientX - startX);
          newHeight = newWidth / originalAspectRatio;
          break;
        case "w": // Ouest (milieu gauche)
          newWidth = startWidth - (moveEvent.clientX - startX);
          newHeight = newWidth / originalAspectRatio;
          break;
      }

      // Appliquer une taille minimale
      newWidth = Math.max(50, newWidth);
      newHeight = Math.max(50, newHeight);

      // Mettre à jour la taille de l'image
      selectedImage.style.width = `${newWidth}px`;
      selectedImage.style.height = `${newHeight}px`;

      // Mettre à jour la position des poignées en temps réel
      updateResizeHandlesPosition(selectedImage);
    };

    const handleMouseUp = () => {
      // Enregistrer les dimensions finales
      resizeImage(`${selectedImage.offsetWidth}px`, `${selectedImage.offsetHeight}px`);

      // Supprimer les écouteurs d'événements
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    // Ajouter les écouteurs d'événements pour l'opération de glisser
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Add handleKeyDown function
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (onSubmit) {
        onSubmit();
      }
    }
  };

  return (
    <div
      className={`tiptap-editor flex flex-col ${height}`}
      style={
        contentHeight
          ? {
              minHeight: Math.max(208, contentHeight + 40) + "px",
              height: "auto",
            }
          : {}
      }>
      <div className="z-10">
        <MenuBar editor={editor} buttonList={buttonList} handleImageUpload={handleImageUpload} />
      </div>
      <div ref={editorContainerRef} className="relative flex-1" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onPaste={handlePaste} onKeyDown={handleKeyDown}>
        <EditorContent editor={editor} className="p-2 prose max-w-none min-h-full" />

        {uploading && (
          <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"></div>
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0.4s" }}></div>
              <span className="text-xs text-blue-600 ml-2">Chargement...</span>
            </div>
          </div>
        )}

        {selectedImage && (
          <>
            {/* 8 poignées de redimensionnement */}
            {/* Coin supérieur gauche (NW) */}
            <div
              className="resize-handle w-3 h-3 bg-white border border-blue-500 rounded-full cursor-nwse-resize absolute"
              style={{
                top: selectedImage.getBoundingClientRect().top - editorContainerRef.current.getBoundingClientRect().top + "px",
                left: selectedImage.getBoundingClientRect().left - editorContainerRef.current.getBoundingClientRect().left + "px",
                transform: "translate(-50%, -50%)",
                zIndex: 50,
              }}
              onMouseDown={(e) => startResize(e, "nw")}
            />

            {/* Milieu supérieur (N) */}
            <div
              className="resize-handle w-3 h-3 bg-white border border-blue-500 rounded-full cursor-ns-resize absolute"
              style={{
                top: selectedImage.getBoundingClientRect().top - editorContainerRef.current.getBoundingClientRect().top + "px",
                left: selectedImage.getBoundingClientRect().left + selectedImage.offsetWidth / 2 - editorContainerRef.current.getBoundingClientRect().left + "px",
                transform: "translate(-50%, -50%)",
                zIndex: 50,
              }}
              onMouseDown={(e) => startResize(e, "n")}
            />

            {/* Coin supérieur droit (NE) */}
            <div
              className="resize-handle w-3 h-3 bg-white border border-blue-500 rounded-full cursor-nesw-resize absolute"
              style={{
                top: selectedImage.getBoundingClientRect().top - editorContainerRef.current.getBoundingClientRect().top + "px",
                left: selectedImage.getBoundingClientRect().right - editorContainerRef.current.getBoundingClientRect().left + "px",
                transform: "translate(-50%, -50%)",
                zIndex: 50,
              }}
              onMouseDown={(e) => startResize(e, "ne")}
            />

            {/* Milieu droit (E) */}
            <div
              className="resize-handle w-3 h-3 bg-white border border-blue-500 rounded-full cursor-ew-resize absolute"
              style={{
                top: selectedImage.getBoundingClientRect().top + selectedImage.offsetHeight / 2 - editorContainerRef.current.getBoundingClientRect().top + "px",
                left: selectedImage.getBoundingClientRect().right - editorContainerRef.current.getBoundingClientRect().left + "px",
                transform: "translate(-50%, -50%)",
                zIndex: 50,
              }}
              onMouseDown={(e) => startResize(e, "e")}
            />

            {/* Coin inférieur droit (SE) */}
            <div
              className="resize-handle w-3 h-3 bg-white border border-blue-500 rounded-full cursor-nwse-resize absolute"
              style={{
                top: selectedImage.getBoundingClientRect().bottom - editorContainerRef.current.getBoundingClientRect().top + "px",
                left: selectedImage.getBoundingClientRect().right - editorContainerRef.current.getBoundingClientRect().left + "px",
                transform: "translate(-50%, -50%)",
                zIndex: 50,
              }}
              onMouseDown={(e) => startResize(e, "se")}
            />

            {/* Milieu inférieur (S) */}
            <div
              className="resize-handle w-3 h-3 bg-white border border-blue-500 rounded-full cursor-ns-resize absolute"
              style={{
                top: selectedImage.getBoundingClientRect().bottom - editorContainerRef.current.getBoundingClientRect().top + "px",
                left: selectedImage.getBoundingClientRect().left + selectedImage.offsetWidth / 2 - editorContainerRef.current.getBoundingClientRect().left + "px",
                transform: "translate(-50%, -50%)",
                zIndex: 50,
              }}
              onMouseDown={(e) => startResize(e, "s")}
            />

            {/* Coin inférieur gauche (SW) */}
            <div
              className="resize-handle w-3 h-3 bg-white border border-blue-500 rounded-full cursor-nesw-resize absolute"
              style={{
                top: selectedImage.getBoundingClientRect().bottom - editorContainerRef.current.getBoundingClientRect().top + "px",
                left: selectedImage.getBoundingClientRect().left - editorContainerRef.current.getBoundingClientRect().left + "px",
                transform: "translate(-50%, -50%)",
                zIndex: 50,
              }}
              onMouseDown={(e) => startResize(e, "sw")}
            />

            {/* Milieu gauche (W) */}
            <div
              className="resize-handle w-3 h-3 bg-white border border-blue-500 rounded-full cursor-ew-resize absolute"
              style={{
                top: selectedImage.getBoundingClientRect().top + selectedImage.offsetHeight / 2 - editorContainerRef.current.getBoundingClientRect().top + "px",
                left: selectedImage.getBoundingClientRect().left - editorContainerRef.current.getBoundingClientRect().left + "px",
                transform: "translate(-50%, -50%)",
                zIndex: 50,
              }}
              onMouseDown={(e) => startResize(e, "w")}
            />
          </>
        )}
      </div>

      <style jsx="true" global="true">{`
        .ProseMirror {
          min-height: 100%;
          height: auto;
        }

        .ProseMirror img {
          max-width: 100%;
          height: auto;
          box-sizing: border-box;
          cursor: pointer;
        }

        .ProseMirror img.selected-image {
          outline: 1px solid #4299e1;
        }

        .resize-handle:hover {
          background-color: #ebf8ff;
        }

        .ProseMirror ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }

        .ProseMirror ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }

        .ProseMirror li {
          margin-bottom: 0.25em;
        }

        .ProseMirror li p {
          margin: 0;
        }
      `}</style>
    </div>
  );
}
